import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Download, ArrowRight } from 'lucide-react';
import { trackAddToCart } from '../hooks/useMetaPixel';
interface FAQItem {
  question: string;
  answer: React.ReactNode;
}
const faqs: FAQItem[] = [{
  question: '¿Y si mi hijo no coopera?',
  answer: <>
        No necesita cooperar desde el día uno. El tablero visual{' '}
        <strong data-id="element-51">reduce la fricción</strong> porque no depende de tu voz. Tu hijo
        empieza a seguir la secuencia <strong data-id="element-52">por inercia</strong>, no por
        obediencia.
      </>
}, {
  question: '¿Funciona con rabietas fuertes?',
  answer: <>
        Sí. Las rutinas visuales no eliminan las emociones, pero{' '}
        <strong data-id="element-53">bajan la intensidad</strong>. Cuando el niño sabe qué viene
        después, hay <strong data-id="element-54">menos sorpresas</strong>. Y menos sorpresas = menos
        estallidos.
      </>
}, {
  question: 'No tengo tiempo.',
  answer: <>
        Implementar la primera rutina toma <strong data-id="element-55">menos de 15 minutos</strong>.
        Imprimes, pegas, listo. El tiempo que hoy pierdes repitiendo,{' '}
        <strong data-id="element-56">lo recuperas desde el primer día</strong>.
      </>
}, {
  question: 'Ya probé cuadros de tareas.',
  answer: <>
        Los cuadros de tareas dependen de premios y motivación externa. Este
        sistema usa <strong data-id="element-57">secuencia visual + neurodesarrollo</strong>. No es
        un sticker chart. Es un{' '}
        <strong data-id="element-58">mapa que el niño puede seguir solo</strong>.
      </>
}, {
  question: '¿Y si no soy constante?',
  answer: <>
        El tablero <strong data-id="element-59">trabaja por ti</strong> incluso en tus peores días.
        No necesitas energía ni ánimo perfecto. Solo necesitas que esté{' '}
        <strong data-id="element-60">visible</strong>. Eso es todo.
      </>
}];
function FAQItem({
  item,
  isOpen,
  onToggle,
  index
}: {
  item: FAQItem;
  isOpen: boolean;
  onToggle: () => void;
  index: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 12
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: index * 0.08
  }} className="border-b border-stone-200 last:border-b-0" data-id="element-61">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-5 px-1 text-left group" data-id="element-62">
        <span className="text-lg font-bold text-stone-800 pr-4 group-hover:text-amber-600 transition-colors" data-id="element-63">
          {item.question}
        </span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.2
      }} className="shrink-0" data-id="element-64">
          <ChevronDown className={`w-5 h-5 transition-colors ${isOpen ? 'text-amber-500' : 'text-stone-400'}`} data-id="element-65" />
        </motion.div>
      </button>

      <AnimatePresence initial={false} data-id="element-66">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-67">
            <p className="pb-5 px-1 text-stone-600 leading-relaxed text-base" data-id="element-68">
              {item.answer}
            </p>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return <section className="py-10 bg-white" data-id="element-69">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-70">
        <div className="max-w-2xl mx-auto" data-id="element-71">
          <div className="text-center mb-8" data-id="element-72">
            <span className="text-amber-500 font-bold text-sm uppercase tracking-widest mb-2 block" data-id="element-73">
              Preguntas frecuentes
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-stone-900" data-id="element-74">
              Antes de decidir, lee esto
            </h2>
          </div>

          <div className="bg-stone-50 rounded-3xl p-6 md:p-8 shadow-sm" data-id="element-75">
            {faqs.map((faq, index) => <FAQItem key={index} item={faq} index={index} isOpen={openIndex === index} onToggle={() => setOpenIndex(openIndex === index ? null : index)} data-id="element-76" />)}
          </div>

          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="text-center mt-6 text-lg text-stone-700 font-semibold" data-id="element-77">
            No necesitas tener todo claro.
            <br data-id="element-78" />
            Solo necesitas{' '}
            <span className="text-amber-500 font-extrabold" data-id="element-79">
              empezar con un tablero
            </span>
            .
          </motion.p>

          <div className="flex justify-center mt-8" data-id="element-80">
            <button onClick={() => {
            trackAddToCart();
            window.open('https://www.reconectarhoy.com/cart/47864918769899:1', '_blank');
          }} className="w-full max-w-md px-8 py-5 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 text-white text-xl font-extrabold rounded-full shadow-[0_0_30px_rgba(239,68,68,0.5)] hover:shadow-[0_0_50px_rgba(239,68,68,0.7)] hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 group animate-pulse" data-id="element-81">
              <Download className="w-6 h-6" data-id="element-82" />
              Descarga Ahora Sistema Completo
              <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-83" />
            </button>
          </div>
        </div>
      </div>
    </section>;
}